import { useEffect, useMemo, useState } from "react";
import { useMetrics, useServiceInstances, type MetricName, type MetricSeries, type ServiceInstance } from "@/lib/api";

export type RangeKey = "1h" | "6h" | "24h" | "7d" | "30d";

export const RANGES: { value: RangeKey; label: string; seconds: number; long: string }[] = [
  { value: "1h", label: "1h", seconds: 3600, long: "the last hour" },
  { value: "6h", label: "6h", seconds: 6 * 3600, long: "the last 6 hours" },
  { value: "24h", label: "24h", seconds: 86400, long: "the last 24 hours" },
  { value: "7d", label: "7d", seconds: 7 * 86400, long: "the last 7 days" },
  { value: "30d", label: "30d", seconds: 30 * 86400, long: "the last 30 days" },
];

export const METRICS: MetricName[] = [
  "cpu",
  "memory",
  "requests",
  "errors_5xx",
  "latency_p50",
  "latency_p95",
  "latency_p99",
  "network_in",
  "network_out",
  "cost",
];

/** Id used for an aggregate series that has no service_id. */
export const ALL_SERVICES = "all-services";

export interface ServiceMetrics {
  id: string;
  name: string;
  kind: ServiceInstance["kind"];
  status: ServiceInstance["status"];
  /** Index into the categorical palette, stable across filters. */
  colorIndex: number;
  series: Partial<Record<MetricName, MetricSeries>>;
}

/** "Now", rounded to the minute and refreshed each minute, so query keys stay stable between renders. */
function useMinuteNow(): number {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 60_000) * 60_000);
  useEffect(() => {
    const t = setInterval(() => setNow(Math.floor(Date.now() / 60_000) * 60_000), 60_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export function useMetricsView(project: string, env: string, range: RangeKey, service: string | undefined) {
  const now = useMinuteNow();
  const seconds = RANGES.find((r) => r.value === range)?.seconds ?? 6 * 3600;
  const from = new Date(now - seconds * 1000).toISOString();
  const to = new Date(now).toISOString();

  const instances = useServiceInstances(project, env);
  const metrics = useMetrics(project, { metric: METRICS, environment: env, from, to, ...(service ? { service } : {}) });

  const view = useMemo(() => {
    const insts = instances.data?.data ?? [];
    const result = metrics.data;
    if (!result) return null;
    // No services in this environment means nothing to chart, whatever the API sends back.
    if (instances.data && insts.length === 0) return { services: [], x: [], step: result.step_seconds ?? 60, from: result.from, to: result.to };
    const order = insts.map((i) => i.service_id);
    const bySvc = new Map<string, ServiceMetrics>();
    for (const s of result.series) {
      // A series without a service is the whole environment added up: name it plainly, never "unknown".
      const id = s.service_id ?? ALL_SERVICES;
      let entry = bySvc.get(id);
      if (!entry) {
        const inst = insts.find((i) => i.service_id === id);
        entry = {
          id,
          name: inst?.name ?? (id === ALL_SERVICES ? "All services" : id),
          kind: inst?.kind,
          status: inst?.status ?? "running",
          colorIndex: Math.max(0, order.indexOf(id)),
          series: {},
        };
        bySvc.set(id, entry);
      }
      entry.series[s.metric] = s;
    }
    const services = [...bySvc.values()].sort((a, b) => a.colorIndex - b.colorIndex);
    const first = result.series[0];
    const x = first ? first.points.map((p) => Math.floor(Date.parse(p.t) / 1000)) : [];
    return { services, x, step: result.step_seconds ?? 60, from: result.from, to: result.to };
  }, [instances.data, metrics.data]);

  return {
    view,
    instances: instances.data?.data ?? [],
    isPending: metrics.isPending || instances.isPending,
    isFetching: metrics.isFetching,
    error: metrics.error ?? instances.error,
    refetch: () => {
      void metrics.refetch();
      void instances.refetch();
    },
  };
}

/** Values of a metric for a service, aligned to x (nulls where missing). */
export function valuesOf(svc: ServiceMetrics, metric: MetricName, length: number): (number | null)[] {
  const pts = svc.series[metric]?.points ?? [];
  const out: (number | null)[] = new Array(length).fill(null);
  for (let i = 0; i < Math.min(length, pts.length); i++) out[i] = pts[i]?.v ?? null;
  return out;
}

/** Sum several aligned series point-by-point (null only when every input is null). */
export function sumSeries(list: (number | null)[][], length: number): (number | null)[] {
  const out: (number | null)[] = new Array(length).fill(null);
  for (const vals of list) {
    for (let i = 0; i < length; i++) {
      const v = vals[i];
      if (v == null) continue;
      out[i] = (out[i] ?? 0) + v;
    }
  }
  return out;
}

export function stats(vals: (number | null)[]) {
  const nums = vals.filter((v): v is number => v != null && Number.isFinite(v));
  if (!nums.length) return { avg: 0, max: 0, min: 0, last: 0, sum: 0, count: 0 };
  const sum = nums.reduce((a, b) => a + b, 0);
  return { avg: sum / nums.length, max: Math.max(...nums), min: Math.min(...nums), last: nums[nums.length - 1]!, sum, count: nums.length };
}
